import * as React from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FieldValues, Path, UseFormReturn, PathValue } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { useListEntities } from 'src/features/entity/api/useListEntities';
import { EntityFormFieldSchemaValues } from 'src/features/entity/formSchema';
import { ENTITY_TYPE } from 'src/features/entity/types';
import { ENTITY_TYPE_TRANSLATION_MAP } from 'src/features/entity/utils/constants';
import { membershipMessages } from 'src/features/membership/utils/messages';
import { useParking } from 'src/providers/ParkingProvider';
import InputFormField from 'src/registry/components/forms/InputFormField';
import SelectFormField from 'src/registry/components/forms/SelectFormField';
import { Button } from 'src/registry/ui/Button';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from 'src/registry/ui/Command';
import { FormControl, FormControlStack, FormItem, FormLabel, FormMessage } from 'src/registry/ui/Form';
import { Popover, PopoverContent, PopoverTrigger } from 'src/registry/ui/Popover';
import { generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';
import { cn } from 'src/utils/shadcn';

interface ComboboxProps<TFormValues extends FieldValues & EntityFormFieldSchemaValues> {
    form: UseFormReturn<TFormValues>;
}

const NEW_ENTITY = 'new_entity';
export default function EntityFormField<TFormValues extends FieldValues & EntityFormFieldSchemaValues>({
    form,
}: ComboboxProps<TFormValues>) {
    const { owner } = form.getValues();
    const [open, setOpen] = React.useState(false);
    const [search, setSearch] = React.useState('');
    const [value, setValue] = React.useState(owner ?? '');
    const { formatMessage } = useIntl();

    const { parkingId } = useParking();
    const { data } = useListEntities({
        parkingId,
        search: search,
    });

    const searchText = React.useMemo(() => {
        if (!data) return null;

        if (value === NEW_ENTITY) return formatMessage(membershipMessages.createEntity);

        return data.results.find((entity) => entity.id === value)?.name ?? null;
    }, [data, formatMessage, value]);

    return (
        <>
            <FormItem variant="dialog">
                <FormLabel variant="dialog">{formatMessage(membershipMessages.entity)}</FormLabel>
                <FormControlStack>
                    <FormControl>
                        <Popover open={open} onOpenChange={setOpen}>
                            <PopoverTrigger asChild>
                                <Button
                                    className="h-auto flex-wrap"
                                    variant="dropdown"
                                    role="combobox"
                                    aria-expanded={open}
                                >
                                    <FontAwesomeIcon
                                        icon={ICON_MAP.CHEVRON_DOWN}
                                        className="text-primary-700 my-1.5 mr-1 size-3 w-4 shrink-0"
                                    />
                                    {searchText ?? (
                                        <p className="text-gray-400">{formatMessage(membershipMessages.entity)}...</p>
                                    )}
                                </Button>
                            </PopoverTrigger>
                            <PopoverContent
                                style={{ width: 'var(--radix-popover-trigger-width)' }}
                                align="start"
                                className="p-0 pb-16"
                                portal={false}
                            >
                                <Command>
                                    <CommandInput
                                        value={search}
                                        onValueChange={setSearch}
                                        placeholder={formatMessage(membershipMessages.searchEntity)}
                                        maxLength={255}
                                    />
                                    <CommandList>
                                        <CommandEmpty className="flex flex-col items-center justify-center p-1 px-4 py-8 text-center text-balance">
                                            <p className="text-sm font-semibold">
                                                {formatMessage(membershipMessages.noEntities)}
                                            </p>
                                            <p className="text-sm text-gray-500">
                                                {formatMessage(membershipMessages.noEntitiesFound)}
                                            </p>
                                        </CommandEmpty>
                                        <CommandGroup className={cn(!data?.results?.length && 'hidden')}>
                                            {data?.results?.map((entity) => (
                                                <CommandItem
                                                    key={entity.id}
                                                    value={entity.id}
                                                    keywords={[entity.name]}
                                                    onSelect={() => {
                                                        form.setValue(
                                                            'entity' as Path<TFormValues>,
                                                            null as PathValue<TFormValues, Path<TFormValues>>,
                                                        );
                                                        form.setValue(
                                                            'owner' as Path<TFormValues>,
                                                            entity.id as PathValue<TFormValues, Path<TFormValues>>,
                                                            { shouldDirty: true },
                                                        );
                                                        setValue(entity.id);
                                                        setOpen(false);
                                                    }}
                                                >
                                                    {entity.name}
                                                </CommandItem>
                                            ))}
                                        </CommandGroup>
                                        <CommandGroup
                                            forceMount
                                            className="absolute bottom-0 left-0 z-50 w-full rounded-b border bg-white"
                                        >
                                            <CommandItem
                                                onSelect={() => {
                                                    setValue(NEW_ENTITY);
                                                    setOpen(false);
                                                }}
                                                className="cursor-pointer"
                                            >
                                                <FontAwesomeIcon
                                                    icon={ICON_MAP.ADD}
                                                    className={cn('text-primary-700 mr-1 size-3 w-4 shrink-0')}
                                                />
                                                {formatMessage(membershipMessages.createEntity)}
                                            </CommandItem>
                                            <CommandItem
                                                onSelect={() => {
                                                    form.setValue(
                                                        'entity' as Path<TFormValues>,
                                                        null as PathValue<TFormValues, Path<TFormValues>>,
                                                    );
                                                    form.setValue(
                                                        'owner' as Path<TFormValues>,
                                                        null as PathValue<TFormValues, Path<TFormValues>>,
                                                        { shouldDirty: true },
                                                    );
                                                    setValue('');
                                                    setOpen(false);
                                                }}
                                                className="cursor-pointer"
                                            >
                                                <FontAwesomeIcon
                                                    icon={ICON_MAP.DELETE}
                                                    className={cn('mr-1 size-3 w-4 shrink-0 text-red-500')}
                                                />
                                                {formatMessage(membershipMessages.clearSelection)}
                                            </CommandItem>
                                        </CommandGroup>
                                    </CommandList>
                                </Command>
                            </PopoverContent>
                        </Popover>
                    </FormControl>
                    <FormMessage />
                </FormControlStack>
            </FormItem>

            {value === NEW_ENTITY && (
                <>
                    <SelectFormField<TFormValues>
                        form={form}
                        name={'entity.type' as Path<TFormValues>}
                        label={formatMessage(membershipMessages.type)}
                        options={Object.values(ENTITY_TYPE)}
                        optionTranslationMap={ENTITY_TYPE_TRANSLATION_MAP}
                    />
                    <InputFormField<TFormValues>
                        form={form}
                        name={'entity.name' as Path<TFormValues>}
                        label={formatMessage(generalMessages.name)}
                    />
                    <InputFormField<TFormValues>
                        required={false}
                        form={form}
                        name={'entity.phone' as Path<TFormValues>}
                        pattern={/^(?:\+?[0-9]+)?$/}
                        label={formatMessage(membershipMessages.phone)}
                    />
                    <InputFormField<TFormValues>
                        required={false}
                        form={form}
                        name={'entity.email' as Path<TFormValues>}
                        label={formatMessage(membershipMessages.email)}
                    />
                    <InputFormField<TFormValues>
                        required={false}
                        form={form}
                        name={'entity.description' as Path<TFormValues>}
                        label={formatMessage(membershipMessages.description)}
                    />
                </>
            )}
        </>
    );
}
